"use client";


import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Copy, X } from "lucide-react";

interface ExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LETTER_TEMPLATE = `Subject: Verification of Enrollment & Reimbursement Support for AI Engineer Bootcamp (Annual), $289

Dear [Manager/Finance Team Name],

This letter confirms that [Member Name] ([Job Title], [Company]) is enrolled in the AI Engineer Bootcamp with AI Builder Club under the annual plan ($289). The plan includes 12-month access to structured curriculum, live workshops, and a practitioner community focused on building production-ready AI features (LLM apps, agents, evaluation, and workflows).

For reimbursement and record-keeping, we provide the following documentation:

Invoice and receipt for $289

Certificate of enrollment (issued immediately)

Certificate of completion (issued after the first week of participation)

Participants apply the training to work-relevant prototypes in week one and continue with guided practice across the year, leading to faster delivery and higher-quality AI work for [Company]. Self-paced lessons plus live sessions (recordings available), designed to minimize disruption to normal work hours.

If your team requires a customized invoice (company name/tax ID), a vendor form, or additional verification, please contact Jason@aibuilderclub.com.

Sincerely,
AI Builder Club
Jason Zhou, Program Lead
Jason@aibuilderclub.com`;

export default function ExpenseModal({ isOpen, onClose }: ExpenseModalProps) {

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-[#1A1A1A] border-white/20">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white">
            How to Expense AI Builder Club for Work
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-8">
          {/* Workflow Section */}
          <div>
            <h3 className="text-xl font-semibold text-white mb-4">
              What You’ll Receive
            </h3>
            <div className="bg-white/5 rounded-lg p-6 border border-white/20 space-y-4 text-left">
              <ul className="space-y-2 text-sm text-white/80">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 text-white/70" />
                  <span>12-month community access</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 text-white/70" />
                  <span>Invoice, receipt, enrollment certificate, and manager letter provided for reimbursement</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 text-white/70" />
                  <span>Completion certificate after your first week</span>
                </li>
              </ul>
              <p className="text-white/60 text-xs">
                If you don’t receive the documents by email, DM @Jason Zhou or @Jaye Mao.
              </p>
            </div>
          </div>

          {/* Why Companies Approve This */}
          <div>
            <h3 className="text-xl font-semibold text-white mb-4">
              Why Companies Approve This Expense
            </h3>
            <div className="bg-white/5 rounded-lg p-6 border border-white/20 space-y-2 text-left">
              <ul className="space-y-2 text-sm text-white/80">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 text-white/70" />
                  <span><strong>Professional development:</strong> Relevant, job-aligned AI skills</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 text-white/70" />
                  <span><strong>Certification included:</strong> Enrollment + completion certificates</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 text-white/70" />
                  <span><strong>Business ROI:</strong> Faster delivery and higher-quality AI work</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5 text-white/70" />
                  <span><strong>12-month access:</strong> Ongoing, structured learning and support</span>
                </li>
              </ul>
            </div>


          </div>

          {/* Certificate Previews */}
          <div>
            <h3 className="text-xl font-semibold text-white mb-4">
              Certificate Previews
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Enrollment Certificate */}
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <h4 className="text-sm font-medium text-white mb-2 text-center whitespace-nowrap">
                  Provided immediately after payment
                </h4>
                <img 
                  src="https://sikkdgaakjhfpachuhjh.supabase.co/storage/v1/object/public/email-content/certifcate%20of%20enrollment.png"
                  alt="AI Engineer Bootcamp Certificate of Enrollment"
                  className="w-full max-w-sm mx-auto rounded-lg shadow-lg object-contain"
                  loading="lazy"
                />
                <p className="text-center text-white/60 text-xs mt-2 whitespace-nowrap overflow-hidden text-ellipsis">
                  Proof of enrollment and 12-month access
                </p>
              </div>

              {/* Completion Certificate */}
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <h4 className="text-sm font-medium text-white mb-2 text-center whitespace-nowrap">
                  Issued after the first week
                </h4>
                <img 
                  src="https://sikkdgaakjhfpachuhjh.supabase.co/storage/v1/object/public/email-content/certificate%20of%20completion%20v2.png"
                  alt="AI Engineer Bootcamp Certificate of Completion"
                  className="w-full max-w-sm mx-auto rounded-lg shadow-lg object-contain"
                  loading="lazy"
                />
                <p className="text-center text-white/60 text-xs mt-2 whitespace-nowrap overflow-hidden text-ellipsis">
                  Official proof of completion
                </p>
              </div>
            </div>
          </div>

          {/* Letter Template */}
          <div>
            <h3 className="text-xl font-semibold text-white mb-4">
              Expense Request Letter Template
            </h3>
            <div className="bg-white/5 rounded-lg p-6">
              <textarea
                value={LETTER_TEMPLATE}
                readOnly
                className="w-full h-64 bg-white/10 text-white/90 text-sm p-4 rounded border border-white/20 resize-none font-mono leading-relaxed"
                onClick={(e) => e.currentTarget.select()}
                placeholder="Letter template will appear here..."
              />
              <p className="text-white/60 text-xs mt-2">
                💡 Click in the text area above to select all text, then Ctrl+C (or Cmd+C) to copy
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <Button
              onClick={() => {
                const textarea = document.querySelector('textarea');
                if (textarea) {
                  textarea.select();
                  textarea.focus();
                }
              }}
              className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
            >
              <Copy className="w-4 h-4 mr-2" />
              Select All Text
            </Button>
            <Button
              variant="outline"
              className="flex-1 border-white/20 text-white hover:bg-white/10"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}